"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";

export interface NewsHeadlineItem {
  id: number;
  slug: string;
  title: string;
  date: string;
  kind: string;
}

const PAGE_SIZE = 20;
const MAX_ITEMS = 200;
const PREFETCH_INDEX = 14;
const ACTIVE_ZONE_PX = 44;

const MASK_GRADIENT = "linear-gradient(to bottom, transparent 0%, #000 14%, #000 86%, transparent 100%)";

export default function HeroNewsFeed({
  initialItems,
  initialHasMore,
  label,
  allNewsLabel,
}: {
  initialItems: NewsHeadlineItem[];
  initialHasMore: boolean;
  label: string;
  allNewsLabel: string;
}) {
  const locale = useLocale();
  const isEn = locale === "en";
  const [items, setItems] = useState<NewsHeadlineItem[]>(initialItems);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasMore, setHasMore] = useState(initialHasMore);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);
  const pageRef = useRef(1);
  const countRef = useRef(initialItems.length);
  const hasMoreRef = useRef(initialHasMore);
  const loadingRef = useRef(false);
  const stoppedRef = useRef(false);

  useEffect(() => {
    countRef.current = items.length;
    hasMoreRef.current = hasMore && items.length < MAX_ITEMS;
  }, [items.length, hasMore]);

  const formatter = new Intl.DateTimeFormat(isEn ? "en-US" : "ru-RU", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });

  // Active headline follows the scroll position (rAF-throttled, single active index).
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const zone = container.getBoundingClientRect().top + ACTIVE_ZONE_PX;
      let next = 0;
      for (let index = 0; index < itemRefs.current.length; index++) {
        const element = itemRefs.current[index];
        if (!element) continue;
        if (element.getBoundingClientRect().top <= zone) next = index;
        else break;
      }
      setActiveIndex((prev) => (prev === next ? prev : next));
    };
    const onScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };
    measure();
    container.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      container.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [items.length]);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || stoppedRef.current) return;
    if (!hasMoreRef.current || countRef.current >= MAX_ITEMS) return;
    loadingRef.current = true;
    try {
      const nextPage = pageRef.current + 1;
      const response = await fetch(
        `/api/articles/feed?locale=${encodeURIComponent(locale)}&page=${nextPage}&pageSize=${PAGE_SIZE}`
      );
      if (!response.ok) throw new Error(`status ${response.status}`);
      const data = (await response.json()) as { items?: NewsHeadlineItem[]; hasMore?: boolean };
      const incoming = data.items ?? [];
      setItems((prev) => {
        const seen = new Set(prev.map((item) => item.id));
        const fresh = incoming.filter((item) => !seen.has(item.id));
        return [...prev, ...fresh].slice(0, MAX_ITEMS);
      });
      pageRef.current = nextPage;
      setHasMore(Boolean(data.hasMore) && countRef.current + incoming.length < MAX_ITEMS);
    } catch {
      stoppedRef.current = true;
      setHasMore(false);
    } finally {
      loadingRef.current = false;
    }
  }, [locale]);

  // Prefetch the next portion once the user approaches the end of the loaded list.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !hasMore) return;
    const target = itemRefs.current[Math.min(PREFETCH_INDEX, items.length - 1)];
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void loadMore();
      },
      { root: container, rootMargin: "240px 0px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [items.length, hasMore, loadMore]);

  return (
    <section aria-label={label} className="relative w-full shrink-0 lg:w-[clamp(340px,26vw,460px)]">
      <h2 className="mb-5 text-[11px] font-medium uppercase tracking-[0.18em] text-white/40">{label}</h2>

      <div className="relative h-[clamp(200px,28vh,240px)] md:h-[clamp(420px,52vh,480px)]">
        <div
          ref={scrollRef}
          tabIndex={0}
          role="region"
          aria-label={label}
          className="h-full overflow-y-auto overscroll-contain rounded-sm outline-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          style={{ maskImage: MASK_GRADIENT, WebkitMaskImage: MASK_GRADIENT }}
        >
          <ul className="flex flex-col pb-[140px] md:pb-[320px]">
            {items.map((item, index) => {
              const isMain = index === activeIndex;
              return (
                <li
                  key={item.id}
                  ref={(element) => {
                    itemRefs.current[index] = element;
                  }}
                  className={`border-b border-white/[0.08] ${isMain ? "py-5" : "py-4"}`}
                >
                  <Link href={getPathWithLocale(`/articles/${item.slug}`, locale)} className="group block">
                    <div className="flex items-center gap-2">
                      <time
                        dateTime={item.date}
                        className={`text-[11px] uppercase tracking-wide transition-colors duration-200 motion-reduce:transition-none ${
                          isMain ? "text-white/50" : "text-white/35"
                        }`}
                      >
                        {formatter.format(new Date(item.date))}
                      </time>
                      {item.kind === "press" && (
                        <span className="text-[10px] text-white/40">{isEn ? "press" : "пресса"}</span>
                      )}
                    </div>
                    <h3
                      className={`mt-1.5 text-balance transition-[font-size,color] duration-200 ease-out motion-reduce:transition-none ${
                        isMain
                          ? "text-[20px] font-semibold leading-[1.25] text-white"
                          : "text-[15px] font-normal leading-[1.35] text-white/70 group-hover:text-white/90"
                      }`}
                    >
                      {item.title}
                    </h3>
                  </Link>
                </li>
              );
            })}

            {!hasMore && (
              <li className="border-t border-white/[0.08] py-5">
                <Link
                  href={`${getPathWithLocale("/articles", locale)}?tab=news`}
                  className="text-[13px] text-white/50 transition-colors hover:text-white/85"
                >
                  {allNewsLabel} →
                </Link>
              </li>
            )}
          </ul>
        </div>

        {/* Progressive blur at the edges: mask/fade is the base, blur is an enhancement */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 hidden h-[72px] supports-[backdrop-filter]:backdrop-blur-[6px] md:block"
          style={{ maskImage: "linear-gradient(to bottom, #000, transparent)", WebkitMaskImage: "linear-gradient(to bottom, #000, transparent)" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-[96px] supports-[backdrop-filter]:backdrop-blur-[6px] md:block"
          style={{ maskImage: "linear-gradient(to top, #000, transparent)", WebkitMaskImage: "linear-gradient(to top, #000, transparent)" }}
        />
      </div>
    </section>
  );
}
